import { Languages } from "lucide-react";
import { useTranslation } from "react-i18next";

import { MoneyText } from "@/components/MoneyText";
import { formatYen } from "@/lib/money";

import {
  BUYER_LOCALE_LABEL,
  BUYER_LOCALES,
  fill,
  NOTICE_TEXTS,
  noticeCode,
  REGIME_TEXTS,
  type BuyerLocale,
} from "./notices";

/**
 * 3) 方式 (design/05 §3.3): the regime tax-service applies today, in a big card —
 * 【本日の方式: 免税価格で販売 → 合計 ¥21,364】 or 【本日の方式: リファンド方式 税込 ¥23,500 →
 * 税関確認後に当店から還付 ¥2,136】 — and what to tell the buyer in the buyer's language
 * (en / ja / 繁 / 简 / th). The amounts are the server's (the ¥5,000 check / the basket).
 * T13-25: the label above the regime carries no date — the switch day is legal-calendar data in
 * tax-service (moved there without a deploy), and the regime shown is the one it answered.
 */
export function RegimeCard({
  regime,
  exemptionPrice,
  total,
  refund,
  noticeKeys,
  buyerLocale,
  onBuyerLocale,
}: {
  regime: "exemption" | "refund";
  /** exemption: the 税抜 price the buyer pays (tax-service's figure). */
  exemptionPrice: number;
  /** refund: the 税込 total paid today and the tax paid back after customs. */
  total: number;
  refund: number;
  noticeKeys: string[];
  buyerLocale: BuyerLocale;
  onBuyerLocale: (locale: BuyerLocale) => void;
}) {
  const { t } = useTranslation();
  const yen = (amount: number) => formatYen(amount, "en");
  const explain =
    regime === "exemption"
      ? fill(REGIME_TEXTS.exemption[buyerLocale], { amount: yen(exemptionPrice) })
      : fill(REGIME_TEXTS.refund[buyerLocale], { total: yen(total), refund: yen(refund) });
  return (
    <div className="flex flex-col gap-3">
      <section
        data-testid="regime-card"
        data-regime={regime}
        className="rounded-lg border-2 border-primary p-4"
      >
        <p className="text-sm font-medium text-muted-foreground">
          {regime === "exemption"
            ? t("taxfree.regime_exemption_period")
            : t("taxfree.regime_refund_period")}
        </p>
        <h2 className="text-2xl font-bold">
          {regime === "exemption" ? t("taxfree.regime_exemption") : t("taxfree.regime_refund")}
        </h2>
        {regime === "exemption" ? (
          <p className="mt-2 flex items-baseline gap-3 text-xl">
            {t("taxfree.exemption_total")}
            <MoneyText amount={exemptionPrice} size="xl" data-testid="regime-total" />
            <span className="text-base text-muted-foreground">{t("taxfree.no_tax")}</span>
          </p>
        ) : (
          <div className="mt-2 flex flex-col gap-1 text-xl">
            <p className="flex items-baseline gap-3">
              {t("taxfree.refund_total")}
              <MoneyText amount={total} size="xl" data-testid="regime-total" />
            </p>
            <p className="flex items-baseline gap-3">
              {t("taxfree.refund_after")}
              <MoneyText amount={refund} size="xl" data-testid="regime-refund" />
            </p>
          </div>
        )}
      </section>
      <section aria-labelledby="buyer-explain" className="rounded-md border p-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 id="buyer-explain" className="flex flex-1 items-center gap-2 font-semibold">
            <Languages aria-hidden className="size-5" />
            {t("taxfree.tell_buyer")}
          </h3>
          {BUYER_LOCALES.map((locale) => (
            <button
              key={locale}
              type="button"
              aria-pressed={buyerLocale === locale}
              onClick={() => onBuyerLocale(locale)}
              className="min-h-10 rounded-md border px-3 aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground"
            >
              {BUYER_LOCALE_LABEL[locale]}
            </button>
          ))}
        </div>
        <div
          lang={buyerLocale}
          className="mt-2 flex flex-col gap-1 text-lg"
          data-testid="buyer-text"
        >
          <p className="font-medium">{explain}</p>
          <ul className="list-disc pl-6">
            {noticeKeys
              .map(noticeCode)
              .filter((code) => NOTICE_TEXTS[code])
              .map((code) => (
                <li key={code}>{NOTICE_TEXTS[code]?.[buyerLocale]}</li>
              ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
